import React, { useCallback, useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";
import { Pagination } from "../components/Pagination";
import Select from "../components/Select";

// Mapeo de type_id a nombre
const TYPE_LABELS = {
  1: "Admin",
  2: "Organizer",
  3: "Team Leader",
  4: "Country Team",
  5: "Observer",
};

const TYPE_OPTIONS = [
  { value: "1", label: "Admin" },
  { value: "2", label: "Organizer" },
  { value: "3", label: "Team Leader" },
  { value: "4", label: "Country Team" },
  { value: "5", label: "Observer" },
];

const SECTION_OPTIONS = [
  { key: "scenathon", label: "Scenathon" },
  { key: "admin_scenathon", label: "Admin Scenathon" },
  { key: "admin_scenathon_list", label: "Admin Scenathon - Scenathons" },
  { key: "admin_scenathon_create", label: "Admin Scenathon - Create New" },
  { key: "admin_scenathon_tasks", label: "Admin Scenathon - Tasks" },
  { key: "admin_scenathon_country_labels", label: "Admin Scenathon - Country Labels" },
  { key: "admin_scenathon_data", label: "Admin Scenathon - Pathways & Scenarios" },
  { key: "admin_scenathon_process", label: "Admin Scenathon - Process" },
  { key: "admin_scenathon_gameslog", label: "Admin Scenathon - Games Log" },
  { key: "admin_scenathon_definedscenarios", label: "Admin Scenathon - Defined Scenarios" },
  { key: "admin_scenathon_tradeoff_indicators", label: "Admin Scenathon - Tradeoff Indicators" },
  { key: "admin_scenathon_users", label: "Admin Scenathon - Users" },
  { key: "pathways", label: "Pathways" },
  { key: "scenarios", label: "Scenarios" },
  { key: "results", label: "Results" },
  { key: "restart_calculator", label: "Restart Calculator" },
  { key: "comparison", label: "Comparison" },
  { key: "trade_adjustment", label: "Trade Adjustment" },
  { key: "ai_assistant", label: "AI Assistant" },
];

const USER_TYPE_ORDER = [1, 2, 3, 4, 5];

function defaultSectionPermissions() {
  return {
    1: [
      "scenathon",
      "admin_scenathon",
      "admin_scenathon_list",
      "admin_scenathon_create",
      "admin_scenathon_tasks",
      "admin_scenathon_country_labels",
      "admin_scenathon_data",
      "admin_scenathon_gameslog",
      "admin_scenathon_definedscenarios",
      "admin_scenathon_tradeoff_indicators",
      "admin_scenathon_users",
      "pathways",
      "scenarios",
      "results",
      "restart_calculator",
      "comparison",
      "trade_adjustment",
      "ai_assistant",
    ],
    2: [
      "scenathon",
      "admin_scenathon",
      "admin_scenathon_process",
      "pathways",
      "scenarios",
      "results",
      "comparison",
      "trade_adjustment",
      "ai_assistant",
    ],
    3: ["scenathon", "pathways", "scenarios", "results", "comparison", "ai_assistant"],
    4: ["scenathon", "pathways", "scenarios", "results", "comparison", "ai_assistant"],
    5: ["scenathon"],
  };
}

export default function Usuarios() {
  const { authFetch } = useAuth();
  const [activeMenu, setActiveMenu] = useState("users");

  const [users, setUsers] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [error, setError] = useState(null);

  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedType, setSelectedType] = useState({});
  const [selectedCountry, setSelectedCountry] = useState({});
  const [selectedEmailVerified, setSelectedEmailVerified] = useState({});
  const [updating, setUpdating] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    first_name: "",
    second_name: "",
    last_name: "",
    email: "",
    organization_name: "",
    country: "",
    type_id: "5",
    password: "",
  });
  const [creating, setCreating] = useState(false);

  const [sectionPermissions, setSectionPermissions] = useState(defaultSectionPermissions);
  const [loadingSections, setLoadingSections] = useState(true);
  const [savingSections, setSavingSections] = useState(false);

  // Search
  const [search, setSearch] = useState("");

  // Sorting
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(6);

  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authFetch("/api/auth/users");

        if (!response.ok) {
          let errorMsg = "Error loading users";
          try {
            const data = await response.json();
            errorMsg = data.error || `Error ${response.status}`;
          } catch {
            errorMsg = `Error ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMsg);
        }

        const data = await response.json();
        const loadedUsers = data.users || [];
        setUsers(loadedUsers);

        const typeMap = {};
        const countryMap = {};
        const emailVerifiedMap = {};

        loadedUsers.forEach((u) => {
          typeMap[u.id] = String(u.type_id ?? "");
          countryMap[u.id] = u.country || "";
          emailVerifiedMap[u.id] = String(Boolean(u.email_verified));
        });

        setSelectedType(typeMap);
        setSelectedCountry(countryMap);
        setSelectedEmailVerified(emailVerifiedMap);
      } catch (err) {
        console.error("Load users error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [authFetch]);

  // Load countries from definedscenarios.location
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);

        const response = await authFetch("/api/auth/locations");

        if (!response.ok) {
          let errorMsg = "Error loading countries";
          try {
            const data = await response.json();
            errorMsg = data.error || `Error ${response.status}`;
          } catch {
            errorMsg = `Error ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMsg);
        }

        const data = await response.json();
        const locations = Array.isArray(data.locations) ? data.locations : [];

        setCountryOptions(
          locations.map((loc) => ({
            value: loc,
            label: loc,
          })),
        );
      } catch (err) {
        console.error("Load countries error:", err);

        // fallback: usar countries actuales de usuarios si falla el endpoint
        const fallbackCountries = new Set();
        users.forEach((u) => {
          if (u.country) fallbackCountries.add(u.country);
        });

        setCountryOptions(
          Array.from(fallbackCountries)
            .sort()
            .map((country) => ({
              value: country,
              label: country,
            })),
        );
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [authFetch, users]);

  useEffect(() => {
    const loadSectionPermissions = async () => {
      try {
        setLoadingSections(true);
        const response = await authFetch("/api/auth/section-permissions");
        if (!response.ok) {
          let msg = "Error loading section permissions";
          try {
            const data = await response.json();
            msg = data.error || msg;
          } catch {}
          throw new Error(msg);
        }

        const data = await response.json();
        const incoming = data?.permissionsByType || {};

        const next = defaultSectionPermissions();
        USER_TYPE_ORDER.forEach((typeId) => {
          const raw = incoming[String(typeId)] ?? incoming[typeId] ?? next[typeId];
          next[typeId] = Array.isArray(raw)
            ? Array.from(new Set(raw.map((s) => String(s || "").trim()).filter(Boolean)))
            : [];
        });

        setSectionPermissions(next);
      } catch (err) {
        console.error("Load section permissions error:", err);
        setSectionPermissions(defaultSectionPermissions());
      } finally {
        setLoadingSections(false);
      }
    };

    loadSectionPermissions();
  }, [authFetch]);

  const toggleSection = (typeId, sectionKey) => {
    setSectionPermissions((prev) => {
      const current = Array.isArray(prev[typeId]) ? prev[typeId] : [];
      const exists = current.includes(sectionKey);
      return {
        ...prev,
        [typeId]: exists
          ? current.filter((s) => s !== sectionKey)
          : [...current, sectionKey],
      };
    });
  };

  const handleSaveSectionPermissions = async () => {
    try {
      setSavingSections(true);
      const response = await authFetch("/api/auth/section-permissions", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissionsByType: sectionPermissions }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "Error saving section permissions");
      }

      window.dispatchEvent(
        new CustomEvent("section-permissions-updated", {
          detail: { permissionsByType: data?.permissionsByType || sectionPermissions },
        }),
      );

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Section permissions updated",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Save section permissions error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setSavingSections(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const resetNewUser = useCallback(() => {
    setNewUser({
      first_name: "",
      second_name: "",
      last_name: "",
      email: "",
      organization_name: "",
      country: "",
      type_id: "5",
      password: "",
    });
  }, []);

  const handleCreateUser = async () => {
    setCreating(true);
    try {
      const response = await authFetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: newUser.first_name,
          second_name: newUser.second_name,
          last_name: newUser.last_name,
          email: newUser.email,
          organization: newUser.organization_name,
          country: newUser.country,
          password: newUser.password,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Error creating user");
      }

      await Swal.fire({
        icon: "success",
        title: "User created",
        text: "The new user was created successfully.",
        confirmButtonColor: "#3085d6",
      });

      setIsCreating(false);
      resetNewUser();
      setUsers((prev) => [
        {
          id: data.id || `new-${Date.now()}`,
          first_name: newUser.first_name,
          second_name: newUser.second_name,
          last_name: newUser.last_name,
          full_name: `${newUser.first_name || ""} ${newUser.last_name || ""}`.trim(),
          email: newUser.email,
          organization_name: newUser.organization_name,
          country: newUser.country,
          type_id: Number(newUser.type_id),
          email_verified: false,
          created_at: new Date().toISOString(),
        },
        ...users,
      ]);
    } catch (err) {
      console.error("Create user error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateModal = () => {
    setIsCreating(false);
    resetNewUser();
  };

  const handleSaveUser = async (userId, user) => {
    const newType = Number(selectedType[userId]);
    const newCountry = selectedCountry[userId] || "";
    const newVerified = selectedEmailVerified[userId] === "true";

    const typeChanged = newType !== Number(user.type_id);
    const countryChanged = newCountry !== (user.country || "");
    const emailVerifiedChanged = newVerified !== Boolean(user.email_verified);

    if (!typeChanged && !countryChanged && !emailVerifiedChanged) {
      await Swal.fire({
        icon: "info",
        title: "Info",
        text: "No changes were made",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const changes = [];
    if (typeChanged) changes.push(`type to ${TYPE_LABELS[newType]}`);
    if (countryChanged) changes.push(`country to ${newCountry || "(empty)"}`);
    if (emailVerifiedChanged) changes.push(`email_verified to ${newVerified ? "verified" : "unverified"}`);

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Confirm",
      text: `Change ${changes.join(" and ")}?`,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#3085d6",
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      setUpdating(userId);

      if (typeChanged) {
        const response = await authFetch(`/api/auth/users/${userId}/type`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type_id: newType }),
        });

        if (!response.ok) {
          let msg = "Error updating type";
          try {
            const data = await response.json();
            msg = data.error || msg;
          } catch {}
          throw new Error(msg);
        }
      }

      if (countryChanged) {
        const response = await authFetch(`/api/auth/users/${userId}/country`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country: newCountry }),
        });

        if (!response.ok) {
          let msg = "Error updating country";
          try {
            const data = await response.json();
            msg = data.error || msg;
          } catch {}
          throw new Error(msg);
        }
      }

      if (emailVerifiedChanged) {
        const response = await authFetch(`/api/auth/users/${userId}/email-verified`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email_verified: newVerified }),
        });

        if (!response.ok) {
          let msg = "Error updating email verification";
          try {
            const data = await response.json();
            msg = data.error || msg;
          } catch {}
          throw new Error(msg);
        }
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                ...(typeChanged && { type_id: newType }),
                ...(countryChanged && { country: newCountry }),
                ...(emailVerifiedChanged && { email_verified: newVerified }),
              }
            : u,
        ),
      );

      setEditingUserId(null);
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "User updated successfully",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Update error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setUpdating(null);
    }
  };

  const filtered = useMemo(() => {
    let result = users;

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (u) =>
          (u.full_name || "").toLowerCase().includes(term) ||
          (u.email || "").toLowerCase().includes(term) ||
          (u.organization_name || "").toLowerCase().includes(term) ||
          (u.country || "").toLowerCase().includes(term),
      );
    }

    if (sortColumn) {
      result = [...result].sort((a, b) => {
        const av = a[sortColumn]?.toString().toLowerCase() ?? "";
        const bv = b[sortColumn]?.toString().toLowerCase() ?? "";
        const dir = sortDirection === "asc" ? 1 : -1;

        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    return result;
  }, [users, search, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageData = filtered.slice((page - 1) * perPage, page * perPage);

  const canCreateUser = true;

  if (loading) {
    return (
      <div className="w-full p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">⚠️ Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-slate-50 shadow-sm w-full h-screen flex flex-col font-montserrat">
      <div className="p-4 md:p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-gray-600">
          Modify the user type and country for {users.length} registered user(s)
        </p>
      </div>

      <div className="px-4 md:px-6 pb-4">
        <div className="bg-white rounded-lg shadow-sm p-2 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveMenu("users")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                activeMenu === "users"
                  ? "bg-[#03203d] text-white shadow-sm"
                  : "bg-white text-slate-600 hover:text-slate-900"
              }`}
            >
              Users table
            </button>
            <button
              type="button"
              onClick={() => setActiveMenu("sections")}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                activeMenu === "sections"
                  ? "bg-[#03203d] text-white shadow-sm"
                  : "bg-white text-slate-600 hover:text-slate-900"
              }`}
            >
              Sections by User Type
            </button>
          </div>
          {activeMenu === "users" && (
            <div className="ml-auto">
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
              >
                Create User
              </button>
            </div>
          )}
        </div>
      </div>


      {activeMenu === "users" && (
        <>
          <div className="px-4 md:px-6 pb-4">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search by name, email, organization or country..."
              />
            </div>
          </div>

          <div className="px-4 md:px-6 pb-4">
            <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
              <div className="min-w-full">
                <table className="min-w-full table-fixed">
                  <colgroup>
                    <col className="w-[16%]" />
                    <col className="w-[16%]" />
                    <col className="w-[12%]" />
                    <col className="w-[14%]" />
                    <col className="w-[14%]" />
                    <col className="w-[15%]" />
                    <col className="w-[10%]" />
                  </colgroup>
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                    <tr>
                      <th
                        onClick={() => handleSort("full_name")}
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      >
                        Name {sortColumn === "full_name" && (sortDirection === "asc" ? "↑" : "↓")}
                      </th>
                      <th
                        onClick={() => handleSort("email")}
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      >
                        Email {sortColumn === "email" && (sortDirection === "asc" ? "↑" : "↓")}
                      </th>
                      <th
                        onClick={() => handleSort("organization_name")}
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      >
                        Organization {sortColumn === "organization_name" && (sortDirection === "asc" ? "↑" : "↓")}
                      </th>
                      <th
                        onClick={() => handleSort("country")}
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      >
                        Country {sortColumn === "country" && (sortDirection === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Email verified
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Current Type
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {pageData.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                          {user.full_name || "N/A"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 break-words">
                          {user.email}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {user.organization_name || "N/A"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {editingUserId === user.id ? (
                            <Select
                              value={selectedCountry[user.id] || ""}
                              onChange={(value) =>
                                setSelectedCountry((prev) => ({
                                  ...prev,
                                  [user.id]: value,
                                }))
                              }
                              options={countryOptions}
                              placeholder={
                                loadingCountries ? "Loading countries..." : "Select country"
                              }
                              disabled={updating === user.id || loadingCountries}
                              className="text-xs"
                            />
                          ) : (
                            user.country || "N/A"
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {editingUserId === user.id ? (
                            <Select
                              value={selectedEmailVerified[user.id] ?? String(Boolean(user.email_verified))}
                              onChange={(value) =>
                                setSelectedEmailVerified((prev) => ({
                                  ...prev,
                                  [user.id]: value,
                                }))
                              }
                              options={[
                                { value: "true", label: "Verified" },
                                { value: "false", label: "Unverified" },
                              ]}
                              placeholder="Select status"
                              className="text-xs"
                            />
                          ) : (
                            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                              user.email_verified
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-rose-100 text-rose-700"
                            }`}>
                              {user.email_verified ? "Verified" : "Unverified"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {editingUserId === user.id ? (
                            <Select
                              value={selectedType[user.id] || String(user.type_id)}
                              onChange={(value) =>
                                setSelectedType((prev) => ({
                                  ...prev,
                                  [user.id]: value,
                                }))
                              }
                              options={TYPE_OPTIONS}
                              placeholder="Select type"
                              disabled={updating === user.id}
                              className="text-xs"
                            />
                          ) : (
                            <span className="inline-flex px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                              {TYPE_LABELS[user.type_id]}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {editingUserId === user.id ? (
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => handleSaveUser(user.id, user)}
                                disabled={updating === user.id}
                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                {updating === user.id ? "Saving..." : "Save"}
                              </button>
                              <button
                                onClick={() => setEditingUserId(null)}
                                disabled={updating === user.id}
                                className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingUserId(user.id);
                                setSelectedType((prev) => ({
                                  ...prev,
                                  [user.id]: String(user.type_id),
                                }));
                                setSelectedCountry((prev) => ({
                                  ...prev,
                                  [user.id]: user.country || "",
                                }));
                              }}
                              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {pageData.length === 0 && (
                  <div className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg">No users to display</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="px-4 md:px-6 pb-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            </div>
          )}

          <div className="px-4 md:px-6 pb-4">
            <div className="text-sm text-gray-600 text-center bg-blue-50 rounded-lg p-4">
              <p>
                Showing <strong>{pageData.length}</strong> of{" "}
                <strong>{filtered.length}</strong> user(s)
                {search.trim() && ` (filtered from ${users.length} total)`}
              </p>
            </div>
          </div>

          {isCreating && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Create new user</h2>
                    <p className="mt-1 text-sm text-slate-500">Fill the fields below to add a new user.</p>
                  </div>
                  <button
                    onClick={handleCloseCreateModal}
                    className="rounded-full bg-slate-100 px-3 py-2 text-slate-600 hover:bg-slate-200"
                  >
                    ×
                  </button>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <label className="space-y-2 text-sm text-slate-700">
                    <span>First name</span>
                    <input
                      value={newUser.first_name}
                      onChange={(event) => setNewUser((prev) => ({ ...prev, first_name: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>

                  <label className="space-y-2 text-sm text-slate-700">
                    <span>Last name</span>
                    <input
                      value={newUser.last_name}
                      onChange={(event) => setNewUser((prev) => ({ ...prev, last_name: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>

                  <label className="space-y-2 text-sm text-slate-700">
                    <span>Email</span>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(event) => setNewUser((prev) => ({ ...prev, email: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>

                  <label className="space-y-2 text-sm text-slate-700">
                    <span>Organization</span>
                    <input
                      value={newUser.organization_name}
                      onChange={(event) => setNewUser((prev) => ({ ...prev, organization_name: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>

                  <label className="space-y-2 text-sm text-slate-700">
                    <span>Country</span>
                    <Select
                      value={newUser.country}
                      onChange={(value) => setNewUser((prev) => ({ ...prev, country: value }))}
                      options={countryOptions}
                      placeholder={loadingCountries ? "Loading countries..." : "Select country"}
                      disabled={loadingCountries}
                      className="w-full"
                    />
                  </label>

                  <label className="space-y-2 text-sm text-slate-700">
                    <span>Password</span>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(event) => setNewUser((prev) => ({ ...prev, password: event.target.value }))}
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={handleCloseCreateModal}
                    className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateUser}
                    disabled={creating}
                    className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {creating ? "Creating..." : "Create user"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeMenu === "sections" && (
        <div className="px-4 md:px-6 pb-4 flex-1">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Sections by User Type</h2>
                <p className="text-sm text-gray-600">Control which sections are visible for each type_id.</p>
              </div>
              <button
                onClick={handleSaveSectionPermissions}
                disabled={loadingSections || savingSections}
                className="px-4 py-2 bg-[#03203d] text-white rounded hover:bg-[#05407b] disabled:opacity-50"
              >
                {savingSections ? "Saving..." : "Save sections"}
              </button>
            </div>

            {loadingSections ? (
              <div className="text-sm text-gray-500">Loading section permissions...</div>
            ) : (
              <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                <div className="overflow-x-auto overflow-y-auto max-h-[62vh]">
                  <table className="w-full min-w-[760px] border-collapse">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Section</th>
                        {USER_TYPE_ORDER.map((typeId) => (
                          <th key={typeId} className="px-4 py-2 text-center text-sm font-semibold text-gray-700">
                            {TYPE_LABELS[typeId]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {SECTION_OPTIONS.map((section) => (
                        <tr key={section.key} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-800 font-medium">{section.label}</td>
                          {USER_TYPE_ORDER.map((typeId) => {
                            const checked = (sectionPermissions[typeId] || []).includes(section.key);
                            return (
                              <td key={`${section.key}-${typeId}`} className="px-4 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSection(typeId, section.key)}
                                  className="h-4 w-4"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}